import { t } from 'i18n';
import React, { useState, useMemo, useEffect } from 'react';
import { useRouter } from 'next/router';
import {
  BuildPcTabs,
  BuildPcSummary,
  BuildPcItem,
  CustomLink,
  MiniSlides,
  PrintBuildPc,
} from '@teko/ecommerce';
import { Description } from '@teko/teko-ecommerce';
import { Loading } from '../../components/Loading';
import {
  useBuildPcState,
  useBuildPcDispatch,
  Configuration,
  useCartV2,
} from '../../contexts';
import { BuildPcCategory, CategoryRepository } from '../../repositories';
import { BuildPcModal } from '../../components/BuildPcModal';
import { useBanners, useSkus, useNewProductCard } from '../../hooks';
import { logger } from '@teko/log';
import { AddItemsProduct } from '@teko/cart/v2';
import {
  BreadcrumbProps,
  Breadcrumbs,
  Modal,
  styled,
  Button,
  Card,
  Col,
  Row,
  Container,
  LazyImage,
} from '@teko/ds-desktop';
import { parseJsonStringSafely } from '@teko/utils';
import { pageDescriptionContent } from './pageDescriptionContent';
import {
  CPU_CATEGORY_ID,
  MAIN_BOARD_CATEGORY_ID,
  SOCKET_ATTRIBUTE_GROUPS_ID,
  CPU_SOCKET_ATTRIBUTE_CODE,
  MAIN_BOARD_SOCKET_ATTRIBUTE_CODE,
} from 'constants/buildpc';

const Spacer = styled.div`
  height: 1rem;
`;
const StyledBuildPcItem = styled(BuildPcItem)`
  margin-bottom: 1rem;
`;
const StyledBuildPcSummary = styled.div`
  position: sticky;
  top: 80px;
  margin-bottom: 1rem;
`;
const StyledContainer = styled(Container)`
  @media print {
    display: none;
  }
`;
const StyledMiniSlides = styled(MiniSlides)`
  margin-top: 1rem;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const StyledDescription = styled(Description)`
  background-color: white;
  margin-bottom: 1rem;
  border-radius: 8px;
`;

export const BuildPcDesktop: React.FunctionComponent = () => {
  const { convertNewProductCard } = useNewProductCard({
    disableHandlerUnder18: true,
  });

  type ProductCardProps = ReturnType<typeof convertNewProductCard>;

  const { onAddMultipleItems, loading: cartLoading } = useCartV2();
  const router = useRouter();
  const [category, setCategory] = useState<BuildPcCategory | null>(null);
  const dispatch = useBuildPcDispatch();
  const state = useBuildPcState();
  const [allSkus, setAllSkus] = useState<string[]>([]);
  const { banners: topSlider, loading: loadingTopSlider } = useBanners(
    'pv-buildpc-slider-banner',
  );
  const { banners: staticBanners } = useBanners('pv-buildpc-static-banner');
  const buildPcLocalVariable = 'buildPc2';
  const getLocalConfigs = () => {
    let result = [{}, {}, {}];
    const lsBuildPcLocalConfigs: string | null =
      typeof window !== 'undefined'
        ? localStorage.getItem(buildPcLocalVariable)
        : null;

    if (
      !!lsBuildPcLocalConfigs &&
      (parseJsonStringSafely(lsBuildPcLocalConfigs) as {}[]).length > 0
    ) {
      result = parseJsonStringSafely(lsBuildPcLocalConfigs) as {}[];
    }
    return result;
  };
  const getAllConfigSkus = (configurations: Configuration[]): string[] => {
    const skus: Array<string> = [];
    configurations.forEach((config) => {
      Object.keys(config)?.forEach((key) => {
        if (!skus.includes(config[key].product?.sku))
          skus.push(config[key].product?.sku);
      });
    });
    return skus;
  };
  const { result, isLoading } = useSkus(allSkus.length ? allSkus : []);

  const convertedProductsData = useMemo<ProductCardProps[]>(() => {
    return result?.products.map(convertNewProductCard);
  }, [result]);

  const changeConfiguration = (index: number): void => {
    dispatch({
      type: 'CHANGE_CONFIGURATION',
      payload: index,
    });
  };

  const onAddConfigurationToCart = async (): Promise<void> => {
    try {
      const configKeys = Object.keys(state.currentConfiguration);
      const products: AddItemsProduct[] = [];
      for (let i = 0; i < configKeys.length; i += 1) {
        const key = configKeys[i];
        const product = convertedProductsData.find(
          (el) => el.sku === state.currentConfiguration[key].product.sku,
        );
        const quantity = state.currentConfiguration[key].quantity;
        if (product)
          products.push({
            sku: product.sku,
            quantity,
            promotionId: product.selectPromotionId,
          });
      }
      await onAddMultipleItems(products);
    } catch (error) {
      logger.log(error);
    }
  };

  useEffect(() => {
    dispatch({
      type: 'SET_CONFIGURATIONS',
      payload: getLocalConfigs(),
    });
  }, []);

  useEffect(() => {
    localStorage.setItem(
      buildPcLocalVariable,
      JSON.stringify(state.configurations),
    );
    setAllSkus(getAllConfigSkus(state.configurations));
  }, [state.currentConfiguration]);

  const isEmpty = useMemo(() => {
    return Object.keys(state.currentConfiguration).length === 0;
  }, [state.currentConfiguration]);
  const total = useMemo(() => {
    if (isEmpty) return 0;
    return Object.keys(state.currentConfiguration).reduce((total, current) => {
      const line = state.currentConfiguration[current];
      const price =
        line?.product?.price?.latestPrice || line?.product?.price?.price || 0;
      return total + price * line.quantity;
    }, 0);
  }, [state.currentConfiguration, isEmpty]);

  const breadCrumbs: BreadcrumbProps[] = [
    {
      link: '/',
      name: t('Trang chủ'),
    },
    {
      disabled: true,
      link: '#',
      name: 'Build Pc',
    },
  ];

  // Specific flow for PV: https://jira.teko.vn/browse/UX4BUYER-6625
  const initialSocketOption = useMemo(() => {
    const getInitialFilter = (target: string, current: string) => {
      if (
        category?.id === target &&
        state.currentConfiguration[current]?.quantity > 0
      ) {
        const product = convertedProductsData.find(
          (p) => p.sku === state.currentConfiguration[current].product.sku,
        );
        const socketOptionValue = product?.attributeGroups?.find((att) =>
          SOCKET_ATTRIBUTE_GROUPS_ID.includes(att.id),
        );

        if (!!socketOptionValue) {
          switch (target) {
            case CPU_CATEGORY_ID:
              return {
                filterCode: CPU_SOCKET_ATTRIBUTE_CODE,
                filterName: socketOptionValue.name,
                optionName: socketOptionValue.value?.trim(),
              };
            case MAIN_BOARD_CATEGORY_ID:
              return {
                filterCode: MAIN_BOARD_SOCKET_ATTRIBUTE_CODE,
                filterName: socketOptionValue.name,
                optionName: socketOptionValue.value?.trim(),
              };
          }
        }
      }
      return null;
    };

    return (
      getInitialFilter(CPU_CATEGORY_ID, MAIN_BOARD_CATEGORY_ID) ||
      getInitialFilter(MAIN_BOARD_CATEGORY_ID, CPU_CATEGORY_ID) ||
      {}
    );
  }, [JSON.stringify(state.currentConfiguration), category]);

  return (
    <>
      <Loading isLoading={isLoading || cartLoading}>
        <StyledContainer>
          <Row justify="start">
            <Breadcrumbs breadcrumbs={breadCrumbs} />
          </Row>
          <StyledMiniSlides
            height={200}
            width={1200}
            isLoading={loadingTopSlider}
            banners={topSlider || []}
          />
          <PageTitle>{'Build PC - Xây dựng cấu hình máy tính'}</PageTitle>
          <Row marginTop="s16" justify="space-between">
            <Col span={8.5} paddingLeft="s8" paddingRight="s8">
              <BuildPcTabs
                onClickTab={(index: number): void => changeConfiguration(index)}
              />
            </Col>
          </Row>
          <Row marginTop="s16">
            <Col span={8.5} paddingLeft="s8" paddingRight="s8">
              {CategoryRepository.all().map((category) => {
                const selectedHardware =
                  state.currentConfiguration[category.id];
                let product = null;
                if (selectedHardware) {
                  product =
                    convertedProductsData.find(
                      (product) => product.sku === selectedHardware.product.sku,
                    ) || selectedHardware.product;
                }
                return (
                  <StyledBuildPcItem
                    key={category.id}
                    title={category.name}
                    icon={category.icon}
                    onIncrease={(): void =>
                      dispatch({
                        type: 'ADD_HARDWARE',
                        payload: {
                          categoryId: category.id,
                          product: selectedHardware.product,
                        },
                      })
                    }
                    onDecrease={(): void =>
                      dispatch({
                        type: 'REDUCE_HARDWARE_QUANTITY',
                        payload: {
                          categoryId: category.id,
                        },
                      })
                    }
                    setQuantity={(value: number): void =>
                      dispatch({
                        type: 'SET_HARDWARE_QUANTITY',
                        payload: {
                          categoryId: category.id,
                          quantity: value,
                        },
                      })
                    }
                    product={product ? product : undefined}
                    actionText={product ? t('Sửa') : t('Chọn')}
                    onClick={(): void => setCategory(category)}
                    quantity={
                      selectedHardware ? selectedHardware.quantity : undefined
                    }
                  />
                );
              })}
            </Col>
            <Col span={3.5}>
              <StyledBuildPcSummary>
                <BuildPcSummary
                  total={total}
                  selectedCount={Object.keys(state.currentConfiguration).length}
                  onAddToCart={onAddConfigurationToCart}
                  onBuy={(): void => {
                    onAddConfigurationToCart().then(() => router.push('/cart'));
                  }}
                />
                <Card marginTop="s16" padding="s16">
                  <CustomLink
                    targetType="_blank"
                    targetPath="https://www.messenger.com/t/phongvuvietnam"
                  >
                    <Button variant="primary" block>
                      {t('Nhận tư vấn từ chuyên gia')}
                    </Button>
                  </CustomLink>
                </Card>
                {staticBanners && !!staticBanners[0] && (
                  <React.Fragment>
                    <Spacer />
                    <CustomLink targetPath={staticBanners[0].targetPath}>
                      <LazyImage
                        hover="zoom"
                        borderRadius={8}
                        src={staticBanners[0].image}
                      />
                    </CustomLink>
                  </React.Fragment>
                )}
              </StyledBuildPcSummary>
            </Col>
          </Row>
          {pageDescriptionContent && (
            <Container>
              <StyledDescription
                className="ck-content"
                content={pageDescriptionContent}
                maxHeight={760}
                showMoreLabel={t('Xem thêm nội dung')}
                showLessLabel={t('Thu gọn nội dung')}
              />
            </Container>
          )}
          <Modal
            width="748px"
            visible={!!category}
            onClose={(): void => setCategory(null)}
            footer={false}
          >
            {category && (
              <BuildPcModal
                onHide={(): void => setCategory(null)}
                category={category}
                initialSocketOption={initialSocketOption}
              />
            )}
          </Modal>
        </StyledContainer>
      </Loading>
      <PrintBuildPc total={total} buildpc={state.currentConfiguration} />
    </>
  );
};
